import Button from '@components/atoms/button';
import FFHModal from '@components/modal';
import { BodyRegular, H2 } from '@components/atoms/typography';
import type { TransferHit } from '@lib/my-team/utils/transfer-hit';

const MODAL_TITLE = "You're taking a hit this GW";

const pluralise = (count: number, noun: string) =>
  count === 1 ? noun : `${noun}s`;

type TransferHitModalProps = {
  isOpen: boolean;
  hit: TransferHit;
  /** Commits the transfers the member has already chosen. */
  onConfirm: () => void;
  onDismiss: () => void;
  isConfirming?: boolean;
};

/**
 * Warns before committing transfers that exceed the free allowance, so the
 * points cost is never a surprise at the deadline (FFH-2998). Mirrors the
 * app's My Team hit dialog: the primary action proceeds, dismissing cancels.
 */
export const TransferHitModal = ({
  isOpen,
  hit,
  onConfirm,
  onDismiss,
  isConfirming = false
}: TransferHitModalProps) => {
  const handleClose = () => {
    if (isConfirming) return;
    onDismiss();
  };

  return (
    <FFHModal open={isOpen} handleClose={handleClose} title={MODAL_TITLE}>
      <div
        className="flex flex-col gap-4 p-6 max-w-[460px]"
        data-cy="transfer-hit-modal"
      >
        <H2>{MODAL_TITLE}</H2>
        <BodyRegular textColor="text-neutral-700">
          You only have {hit.free} free {pluralise(hit.free, 'transfer')}, but
          you&apos;ve made {hit.made}. If you proceed with these transfers
          beyond the deadline of the GW, you&apos;ll be taking a{' '}
          <span className="font-bold">{hit.points}pt hit</span>.
        </BodyRegular>

        <div className="flex flex-col-reverse gap-2.5 md:flex-row md:justify-end">
          <Button
            variant="secondary"
            onClick={handleClose}
            disabled={isConfirming}
            dataCy="transfer-hit-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={isConfirming}
            dataCy="transfer-hit-confirm"
          >
            I understand
          </Button>
        </div>
      </div>
    </FFHModal>
  );
};
